/**
 * Invoice List Column Definitions
 * Extracted from InvoiceList to keep component under 400 lines.
 */

import React from 'react';
import { Column } from '../../shared/components';

export const DISPLAY_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK'];

export const EXPENSE_CATEGORY_KEYS = [
    'food', 'fuel', 'accommodation', 'transport', 'toll',
    'parking', 'office_supplies', 'communication', 'other',
] as const;

/** Maps raw category key → translation key */
export const CATEGORY_KEY_MAP: Record<string, string> = {
    food: 'catFood',
    fuel: 'catFuel',
    accommodation: 'catAccommodation',
    transport: 'catTransport',
    toll: 'catToll',
    parking: 'catParking',
    office_supplies: 'catOfficeSupplies',
    communication: 'catCommunication',
    other: 'catOther',
};

export function getInvoiceColumns(
    formatCurrency: (amount?: number, currency?: string) => string,
    t: (key: string) => string
): Column[] {
    return [
        {
            key: 'filename',
            title: t('colFilename'),
            type: 'text',
            sortable: true,
            width: '200px',
            render: (value: string) => (
                <span title={value} className="filename-cell">{value}</span>
            )
        },
        {
            key: 'vendor_name',
            title: t('colSupplier'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '180px',
            render: (value: string) => <span title={value || '-'}>{value || '-'}</span>
        },
        {
            key: 'expense_category',
            title: t('colExpenseCategory'),
            type: 'dropdown',
            sortable: true,
            filterable: true,
            editable: true,
            width: '140px',
            options: EXPENSE_CATEGORY_KEYS.map(key => ({
                value: key,
                label: t(CATEGORY_KEY_MAP[key])
            })),
            render: (value: string) => {
                const labelKey = CATEGORY_KEY_MAP[value];
                return <span className={`category-badge category-${value || 'other'}`}>{labelKey ? t(labelKey) : '-'}</span>;
            }
        },
        {
            key: 'supplier_tax_id',
            title: t('colTaxId'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: string) => value || '-'
        },
        {
            key: 'supplier_address',
            title: t('colAddress'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '200px',
            defaultHidden: true,
            render: (value: string) => <span title={value || '-'}>{value || '-'}</span>
        },
        {
            key: 'invoice_number',
            title: t('colInvoiceNumber'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '120px',
            render: (value: string) => value || '-'
        },
        {
            key: 'invoice_date',
            title: t('colInvoiceDate'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '110px',
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return value; }
            }
        },
        {
            key: 'due_date',
            title: t('colDueDate'),
            type: 'text',
            sortable: true,
            editable: true,
            width: '110px',
            defaultHidden: true,
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return value; }
            }
        },
        {
            key: 'total_amount',
            title: t('colTotalAmount'),
            type: 'number',
            sortable: true,
            editable: true,
            width: '130px',
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'currency',
            title: t('colCurrency'),
            type: 'dropdown',
            options: DISPLAY_CURRENCIES,
            sortable: true,
            editable: true,
            width: '90px',
            render: (value: string) => value || 'TRY'
        },
        {
            key: 'total_tax_amount',
            title: t('colTax'),
            type: 'number',
            sortable: true,
            editable: true,
            width: '110px',
            defaultHidden: true,
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'net_amount',
            title: t('colNetAmount'),
            type: 'number',
            sortable: true,
            editable: true,
            width: '120px',
            defaultHidden: true,
            render: (value: number, row: any) => {
                const currency = row.extracted_data?.financials?.currency || row.extracted_data?.currency || 'TRY';
                return formatCurrency(value, currency);
            }
        },
        {
            key: 'line_items_count',
            title: t('colLineItems'),
            type: 'number',
            sortable: true,
            width: '100px',
            render: (value: number) => value > 0 ? value : '-'
        },
        {
            key: 'created_at',
            title: t('colCreatedAt'),
            type: 'date',
            sortable: true,
            width: '100px',
            render: (value: string) => {
                if (!value) return '-';
                try { return new Date(value).toLocaleDateString('tr-TR'); }
                catch { return '-'; }
            }
        }
    ];
}
